import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap, map, of, forkJoin } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { API_BASE_URL, ApiEndpoints } from '@core/config/api.config';
import { skipGlobalLoading } from '@core/interceptors/loading-context';

export interface UserCreate {
  countryId?: string;
  partnerId?: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
}

export interface UserUpdate {
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface UserPasswordChange {
  newPassword: string;
}

export interface UserMePasswordChange {
  currentPassword: string;
  newPassword: string;
}

export interface UserMeUpdate {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'SUPER_ADMIN' | 'COUNTRY_ADMIN' | 'PARTNER_USER';
  country?: {
    id: string;
    name?: string;
  };
  partner?: {
    id: string;
    name?: string;
  };
  status: 'ACTIVE' | 'BLOCKED';
  createdAt: string;
  updatedAt: string;
}

export interface UserListParams {
  offset?: number;
  limit?: number;
  countryId?: string;
  partnerId?: string;
  role?: string;
  filter?: string;
  /** Campo de ordenación según OpenAPI: name, createdAt, updatedAt, counts.countries, counts.partners, email, firstName, lastName */
  sortBy?:
    | 'name'
    | 'createdAt'
    | 'updatedAt'
    | 'counts.countries'
    | 'counts.partners'
    | 'email'
    | 'firstName'
    | 'lastName';
  sortOrder?: 'ASC' | 'DESC';
}

export interface UserListResponse {
  limit: number;
  offset: number;
  total: number;
  items: User[];
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Crea un nuevo usuario
   * POST /users
   */
  create(userData: UserCreate): Observable<User> {
    return this.http.post<User>(ApiEndpoints.users.base, userData).pipe(
      tap({
        error: (error: any) => console.error('❌ User Creation Error:', error),
      }),
    );
  }

  /**
   * Obtiene el listado de usuarios con paginación
   * GET /users
   */
  getAll(params?: UserListParams): Observable<UserListResponse> {
    let httpParams = new HttpParams();

    // Valores por defecto para paginación
    httpParams = httpParams.set('offset', (params?.offset ?? 0).toString());
    httpParams = httpParams.set('limit', (params?.limit ?? 10).toString());

    if (params?.countryId) {
      httpParams = httpParams.set('countryId', params.countryId);
    }
    if (params?.partnerId) {
      httpParams = httpParams.set('partnerId', params.partnerId);
    }
    if (params?.role) {
      httpParams = httpParams.set('role', params.role);
    }
    if (params?.filter) {
      httpParams = httpParams.set('filter', params.filter);
    }
    if (params?.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http
      .get<UserListResponse>(ApiEndpoints.users.base, {
        params: httpParams,
        context: skipGlobalLoading(),
      })
      .pipe(
        tap({
          error: (error: any) => console.error('❌ Users List Error:', error),
        }),
      );
  }

  /**
   * Obtiene un usuario por su ID
   * GET /users/{id}
   */
  getById(id: string): Observable<User> {
    return this.http.get<User>(ApiEndpoints.users.byId(id)).pipe(
      tap({
        error: (error: any) => console.error('❌ User Get Error:', error),
      }),
    );
  }

  /**
   * Actualiza un usuario
   * PUT /users/{id}
   */
  update(id: string, userData: UserUpdate): Observable<User> {
    return this.http.put<User>(ApiEndpoints.users.byId(id), userData).pipe(
      tap({
        error: (error: any) => console.error('❌ User Update Error:', error),
      }),
    );
  }

  /**
   * Cambia la contraseña de un usuario
   * POST /users/{userId}/set-password
   */
  changePassword(id: string, passwordChange: UserPasswordChange): Observable<User> {
    return this.http.post<User>(ApiEndpoints.users.changePassword(id), passwordChange).pipe(
      tap({
        error: (error: any) => console.error('❌ User Change Password Error:', error),
      }),
    );
  }

  /**
   * Cambia la contraseña del propio usuario
   * POST /users/me/change-password
   */
  changeMePassword(passwordChange: UserMePasswordChange): Observable<void> {
    return this.http.post<void>(ApiEndpoints.users.meChangePassword, passwordChange).pipe(
      tap({
        error: (error: any) => console.error('❌ User Change Me Password Error:', error),
      }),
    );
  }

  /**
   * Actualiza los datos del propio usuario
   * PUT /users/me
   */
  updateMe(userData: UserMeUpdate): Observable<void> {
    return this.http.put<void>(ApiEndpoints.users.me, userData).pipe(
      tap({
        error: (error: any) => console.error('❌ User Update Me Error:', error),
      }),
    );
  }

  /**
   * Elimina un usuario
   * DELETE /users/{id}
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(ApiEndpoints.users.byId(id)).pipe(
      tap({
        error: (error: any) => console.error('❌ User Delete Error:', error),
      }),
    );
  }

  /**
   * Desbloquea un usuario
   * POST /users/{userId}/unblock
   */
  unblock(id: string): Observable<User> {
    return this.http.post<User>(ApiEndpoints.users.unblock(id), {}).pipe(
      tap({
        error: (error: any) => console.error('❌ User Activate Error:', error),
      }),
    );
  }

  /**
   * Bloquea un usuario
   * POST /users/{userId}/block
   */
  block(id: string): Observable<User> {
    return this.http.post<User>(ApiEndpoints.users.block(id), {}).pipe(
      tap({
        error: (error: any) => console.error('❌ User Deactivate Error:', error),
      }),
    );
  }

  /**
   * Obtiene usuarios paginados para exportación (método interno)
   */
  private getUsersPaginated(
    offset: number,
    limit: number,
    params: UserListParams,
  ): Observable<UserListResponse> {
    let httpParams = new HttpParams();

    if (offset !== undefined) {
      httpParams = httpParams.set('offset', offset.toString());
    }
    if (limit !== undefined) {
      httpParams = httpParams.set('limit', limit.toString());
    }
    if (params?.countryId) {
      httpParams = httpParams.set('countryId', params.countryId);
    }
    if (params?.partnerId) {
      httpParams = httpParams.set('partnerId', params.partnerId);
    }
    if (params?.role) {
      httpParams = httpParams.set('role', params.role);
    }
    if (params?.filter) {
      httpParams = httpParams.set('filter', params.filter);
    }
    if (params?.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http.get<UserListResponse>(ApiEndpoints.users.base, { params: httpParams }).pipe(
      tap({
        error: (error: any) => console.error('❌ Users List Error:', error),
      }),
    );
  }

  /**
   * Obtiene todos los usuarios para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExport(params?: UserListParams): Observable<User[]> {
    return this.getUsersPaginated(0, 100, params || {}).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<UserListResponse>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getUsersPaginated(offset, 100, params || {}));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allUsers: User[] = [];
            responses.forEach((response) => {
              allUsers.push(...response.items);
            });
            return allUsers;
          }),
        );
      }),
    );
  }
}
